//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect } from 'react';
import { ChevronUp, Edit, Check, X, Minus } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../../ui/collapsible';
import type { PlanApprovalRequest } from '../../../types/streaming';

export type PlanApprovalDecision =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'changes_requested'
  | 'expired';

interface PlanApprovalCardProps {
  plan: PlanApprovalRequest | null;
  // 'pending' (the default) renders the live, interactive card. Any other
  // value is a decided historical record: collapsed by default, read-only,
  // no action buttons — re-expandable to review the content, same as a
  // completed ToolCallBox entry.
  decision?: PlanApprovalDecision;
  onApprove: () => void;
  onReject: () => void;
  onRequestChanges: (feedback: string) => void;
}

const DECISION_LABEL: Record<
  Exclude<PlanApprovalDecision, 'pending'>,
  string
> = {
  approved: 'Plan Approved',
  rejected: 'Plan Rejected',
  changes_requested: 'Changes Requested',
  expired: 'No Longer Pending',
};

// aria-hidden — these are decorative once CollapsibleTrigger already
// carries aria-label={DECISION_LABEL[decision]}; a second accessible name
// here would make screen readers double-announce the decision.
const DecisionIcon: React.FC<{
  decision: Exclude<PlanApprovalDecision, 'pending'>;
}> = ({ decision }) => {
  if (decision === 'approved') {
    return (
      <Check className="size-3.5 shrink-0 text-green-600" aria-hidden="true" />
    );
  }
  if (decision === 'rejected') {
    return <X className="size-3.5 shrink-0 text-red-600" aria-hidden="true" />;
  }
  if (decision === 'changes_requested') {
    return (
      <Edit className="size-3.5 shrink-0 text-amber-600" aria-hidden="true" />
    );
  }
  return (
    <Minus className="size-3.5 shrink-0 text-slate-400" aria-hidden="true" />
  );
};

const PlanApprovalCard: React.FC<PlanApprovalCardProps> = ({
  plan,
  decision = 'pending',
  onApprove,
  onReject,
  onRequestChanges,
}) => {
  const [committed, setCommitted] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Reset internal state whenever a new plan object arrives so a second
  // plan_approval_request (e.g. after request-changes) always starts fresh,
  // regardless of whether the parent unmounts/remounts via key.
  useEffect(() => {
    setCommitted(false);
    setShowFeedback(false);
    setFeedback('');
  }, [plan]);

  if (!plan) return null;

  const handleApprove = () => {
    setCommitted(true);
    onApprove();
  };

  const handleReject = () => {
    setCommitted(true);
    onReject();
  };

  const handleSendFeedback = () => {
    setCommitted(true);
    onRequestChanges(feedback);
  };

  const isPending = decision === 'pending';

  const body = (
    <>
      {/* Intent */}
      <p className="mb-3 text-sm">{plan.intent}</p>

      {/* Referents — one row per item, each carrying its own domain so the
          user can see, per item, which domain acts on it (rather than only
          a plan-wide domain list). */}
      <div className="mb-3 max-h-[240px] overflow-auto">
        <table className="w-full text-sm" data-testid="plan-referents">
          <thead>
            <tr className="border-b border-gray-300 dark:border-gray-600">
              <th className="px-2 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                Name
              </th>
              <th className="px-2 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                ID
              </th>
              <th className="px-2 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                Domain
              </th>
              <th className="px-2 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {plan.referents.map((ref, i) => (
              <tr
                key={`${ref.id}-${i}`}
                className="border-b border-gray-200 last:border-0 dark:border-gray-700"
              >
                <td className="px-2 py-2 font-medium text-gray-700 dark:text-gray-300">
                  {ref.name}
                </td>
                <td className="px-2 py-2 text-gray-500 dark:text-gray-400">
                  {ref.id}
                </td>
                <td className="px-2 py-2">
                  <span className="inline-block rounded border border-orange-300 bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                    {ref.domain}
                  </span>
                </td>
                <td className="px-2 py-2">
                  <span className="inline-block rounded bg-gray-200 px-2 py-1 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                    {ref.action}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Out-of-scope boundary — what this plan should NOT do, so the user
          can judge the boundary rather than only what it asks to do.
          "should" not "will": this is model-stated intent, not an enforced
          guarantee — the UI label below already said "Should NOT Do", this
          comment was just stale. */}
      <div
        className="mb-3 rounded border border-border bg-muted/30 p-2"
        data-testid="plan-out-of-scope"
      >
        <span className="mb-1 block text-xs text-slate-500">
          Should NOT Do:
        </span>
        <p className="text-sm">{plan.out_of_scope}</p>
      </div>
    </>
  );

  if (isPending) {
    return (
      <Card className="my-1 rounded-lg border-2 border-orange-400">
        <CardContent className="p-4">
          <p className="mb-2 text-sm font-bold">Plan Approval Required</p>

          {body}

          {/* Request Changes feedback area */}
          {showFeedback && (
            <div className="mb-2">
              <Textarea
                rows={2}
                placeholder="Describe what you'd like changed..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                aria-label="feedback"
              />
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-2">
            {!showFeedback && (
              <Button
                variant="default"
                size="sm"
                disabled={committed}
                onClick={handleApprove}
              >
                Approve
              </Button>
            )}
            {!showFeedback ? (
              <Button
                variant="outline"
                size="sm"
                disabled={committed}
                onClick={() => setShowFeedback(true)}
              >
                Request Changes
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={committed}
                  onClick={() => {
                    setShowFeedback(false);
                    setFeedback('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={committed || !feedback.trim()}
                  onClick={handleSendFeedback}
                >
                  Send Feedback
                </Button>
              </>
            )}
            {!showFeedback && (
              <Button
                variant="outline"
                size="sm"
                disabled={committed}
                className="border-red-600 text-red-600 hover:bg-red-50 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-900/20"
                onClick={handleReject}
              >
                Reject
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Decided — collapsed by default, read-only, no action buttons. Still
  // re-expandable to review what was in the plan, same as a ToolCallBox.
  return (
    <Card className="my-1 overflow-hidden rounded-lg border-2 border-orange-400 p-0">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger
          className="flex w-full cursor-pointer select-none items-center gap-2 border-none bg-transparent px-4 py-3 text-left [&[data-state=open]>svg:last-child]:rotate-0"
          aria-label={DECISION_LABEL[decision]}
        >
          <span className="flex-1 text-sm font-bold">
            {DECISION_LABEL[decision]}
          </span>
          <DecisionIcon decision={decision} />
          <ChevronUp
            className="size-3.5 shrink-0 rotate-180 text-slate-500 transition-transform duration-[280ms]"
            aria-hidden="true"
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t border-orange-200 dark:border-orange-800">
          <CardContent className="p-4">{body}</CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default PlanApprovalCard;
